
import React, { useState } from 'react'
import { motion } from 'motion/react'
import { Icon } from '@/components/ui/icon'
import { Slider } from '@/components/ui/slider'
import { NumberInput } from '@/components/ui/number-input'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { MergedParameter } from '@/lib/ai/parameter-merge'
import type { IconName } from '@/components/ui/icon'
import { springSnappy } from '@/lib/animations'
import { cn } from '@/lib/utils'

const CUSTOM_VALUE_SENTINEL = '__custom__'

/** Visual preview of aspect ratio as a small rectangle */
function AspectRatioPreview({ ratio }: { ratio: string }) {
  const [w, h] = ratio.split(':').map(Number)
  if (!w || !h) return null

  const maxSize = 24
  const aspect = w / h
  const width = aspect >= 1 ? maxSize : Math.round(maxSize * aspect)
  const height = aspect <= 1 ? maxSize : Math.round(maxSize / aspect)

  return (
    <div
      className="rounded-sm border border-current opacity-60"
      style={{ width, height }}
    />
  )
}

interface ParameterControlProps {
  parameter: MergedParameter
  value: unknown
  onChange: (value: unknown) => void
  disabled?: boolean
}

/** Keep backend keys out of the UI when a provider returns them as labels. */
function displayParameterLabel(label: string) {
  const normalized = label.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')
  if (!normalized) return normalized
  if (label === label.toLocaleUpperCase() || /[_-]/.test(label)) {
    const words = normalized.toLocaleLowerCase().split(' ')
    const acronyms = new Set(['api', 'cfg', 'clip', 'fps', 'ip', 'lora', 'sdxl', 'vae'])
    return words.map((word, index) => acronyms.has(word) ? word.toLocaleUpperCase() : index === 0 ? word[0].toLocaleUpperCase() + word.slice(1) : word).join(' ')
  }
  return normalized[0].toLocaleUpperCase() + normalized.slice(1)
}

/** Shared single-line label; long labels truncate visually and remain available in full. */
function ParameterLabel({ parameter, icon, label = displayParameterLabel(parameter.label) }: { parameter: MergedParameter; icon: IconName; label?: string }) {
  const labelClass = 'flex min-w-0 basis-[9rem] grow items-center gap-2'
  const text = <span aria-label={label} title={label} className="min-w-0 truncate text-sm font-medium">{label}</span>
  if (!parameter.description) {
    return (
      <div className={labelClass}>
        <Icon name={icon} className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        {text}
      </div>
    )
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(labelClass, 'cursor-help')}>
            <Icon name={icon} className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {text}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1">
            <div className="font-semibold">{label}</div>
            <div className="text-xs text-muted-foreground whitespace-normal">
              {parameter.description}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** A shared right-hand control column keeps dropdowns, number fields, and toggles aligned. */
function ParameterRow({ parameter, icon, label, children }: {
  parameter: MergedParameter
  icon: IconName
  label: string
  children: React.ReactNode
}) {
  return (
    <div
      className="grid min-w-0 items-center gap-3"
      style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(min(7.5rem, 55%), min(11.25rem, 55%))' }}
    >
      <ParameterLabel parameter={parameter} icon={icon} label={label} />
      <div role="group" aria-label={label} className="flex min-w-0 items-center justify-end">
        {children}
      </div>
    </div>
  )
}

/**
 * Select-or-Input hybrid control
 * Shows a select dropdown with an option to enter a custom value
 */
interface SelectOrInputProps {
  parameter: MergedParameter
  options: unknown[]
  currentValue: string
  isInitiallyCustom: boolean
  onChange: (value: unknown) => void
  disabled: boolean
}

function SelectOrInputControl({
  parameter,
  options,
  currentValue,
  isInitiallyCustom,
  onChange,
  disabled,
}: SelectOrInputProps) {
  const [isCustomMode, setIsCustomMode] = useState(isInitiallyCustom)
  const label = displayParameterLabel(parameter.label)

  const handleSelectChange = (val: string) => {
    if (val === CUSTOM_VALUE_SENTINEL) {
      setIsCustomMode(true)
      // Don't change the value yet — let the user type
    } else {
      setIsCustomMode(false)
      onChange(parameter.type === 'number' || parameter.type === 'integer' ? Number(val) : val)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    onChange(parameter.type === 'number' || parameter.type === 'integer' ? Number(raw) : raw)
  }

  if (options.length === 0) {
    // No enum — just render an input
    return (
      <div className="space-y-2">
        <ParameterLabel parameter={parameter} icon="text" />
        <Input
          value={currentValue}
          onChange={handleInputChange}
          aria-label={label}
          placeholder={parameter.description || ''}
          disabled={disabled}
          className="w-full"
        />
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <ParameterRow parameter={parameter} icon="list" label={label}>
        <Select
          value={isCustomMode ? CUSTOM_VALUE_SENTINEL : currentValue}
          onValueChange={handleSelectChange}
          disabled={disabled}
        >
          <SelectTrigger aria-label={label} className="min-w-0 w-full [&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:overflow-hidden [&>[data-slot=select-value]]:text-ellipsis [&>[data-slot=select-value]]:whitespace-nowrap [&>svg]:shrink-0">
            <SelectValue className="min-w-0 truncate" placeholder="Select..." title={currentValue} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={String(option)} value={String(option)}>
                {String(option)}
              </SelectItem>
            ))}
            <SelectItem value={CUSTOM_VALUE_SENTINEL}>
              Custom...
            </SelectItem>
          </SelectContent>
        </Select>
      </ParameterRow>
      {isCustomMode && (
        <Input
          type={parameter.type === 'number' || parameter.type === 'integer' ? 'number' : 'text'}
          value={currentValue}
          onChange={handleInputChange}
          aria-label={label}
          placeholder={`Enter custom ${label.toLowerCase()}...`}
          min={parameter.minimum}
          max={parameter.maximum}
          disabled={disabled}
          className="w-full"
          autoFocus
        />
      )}
    </div>
  )
}

/**
 * Dynamic parameter control renderer
 * Renders appropriate UI component based on parameter.uiType
 */
export const ParameterControl = React.memo(function ParameterControl({
  parameter,
  value,
  onChange,
  disabled = false,
}: ParameterControlProps) {
  // Policy enforcement: hidden parameters are not rendered
  if (parameter.policy?.visibility === 'hidden') return null

  // Policy enforcement: readonly/non-editable parameters are disabled with lock indicator
  const isLocked = parameter.policy?.visibility === 'readonly' || parameter.policy?.userEditable === false
  const effectiveDisabled = disabled || isLocked

  // Use default value if current value is undefined
  const currentValue = value !== undefined ? value : parameter.default
  const label = displayParameterLabel(parameter.label)

  // Wrap rendered control with subtle tap animation
  const wrapWithMotion = (content: React.ReactNode) => (
    <motion.div
      whileTap={{ scale: 1.01 }}
      transition={springSnappy}
    >
      {content}
    </motion.div>
  )

  // Wrap content with lock indicator if policy-locked
  const wrapWithLockIndicator = (content: React.ReactNode) => {
    if (!isLocked) return wrapWithMotion(content)
    const lockReason = parameter.policy?.lockReason || 'This parameter is locked by the platform'
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative opacity-50 pointer-events-none select-none">
              {content}
              <div className="absolute top-0 right-0 flex items-center gap-1 bg-muted/80 rounded px-1 py-0.5">
                <Icon name="lock" className="h-3 w-3 text-muted-foreground" />
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs pointer-events-auto">
            <p className="text-xs">{lockReason}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  // Render based on UI type
  switch (parameter.uiType) {
    // ============================================================================
    // SLIDER
    // ============================================================================
    case 'slider': {
      const min = parameter.minimum ?? 0
      const max = parameter.maximum ?? 100
      const step = parameter.type === 'integer' ? 1 : 0.1
      const safeValue = (currentValue ?? min) as number

      return wrapWithLockIndicator(
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <ParameterLabel parameter={parameter} icon="sliders" />
            <span className="font-mono text-sm font-medium tabular-nums">{safeValue}</span>
          </div>
          <Slider
            value={[safeValue]}
            onValueChange={([val]) => onChange(val)}
            min={min}
            max={max}
            step={step}
            disabled={effectiveDisabled}
            className="w-full"
          />
        </div>
      )
    }

    // ============================================================================
    // NUMBER INPUT
    // ============================================================================
    case 'number': {
      const min = parameter.minimum
      const max = parameter.maximum
      const step = parameter.type === 'integer' ? 1 : 0.1
      const safeValue = (currentValue ?? min ?? 0) as number

      return wrapWithLockIndicator(
        <ParameterRow parameter={parameter} icon="hash" label={label}>
          <NumberInput
            value={safeValue}
            onChange={onChange}
            min={min}
            max={max}
            step={step}
            disabled={effectiveDisabled}
            title={label}
            className="w-full min-w-0 max-w-full"
          />
        </ParameterRow>
      )
    }

    // ============================================================================
    // SELECT DROPDOWN
    // ============================================================================
    case 'select': {
      const options = parameter.enum || []

      return wrapWithLockIndicator(
        <ParameterRow parameter={parameter} icon="list" label={label}>
          <Select
            value={String(currentValue)}
            onValueChange={(val) => {
              const typed = parameter.type === 'number' || parameter.type === 'integer' ? Number(val) : val
              onChange(typed)
            }}
            disabled={effectiveDisabled}
          >
            <SelectTrigger aria-label={label} className="min-w-0 w-full [&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:overflow-hidden [&>[data-slot=select-value]]:text-ellipsis [&>[data-slot=select-value]]:whitespace-nowrap [&>svg]:shrink-0">
              <SelectValue className="min-w-0 truncate" placeholder="Select..." title={String(currentValue ?? '')} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={String(option)} value={String(option)}>
                  {String(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ParameterRow>
      )
    }

    // ============================================================================
    // TOGGLE / SWITCH
    // ============================================================================
    case 'toggle': {
      return wrapWithLockIndicator(
        <ParameterRow parameter={parameter} icon="toggle-left" label={label}>
          <Switch
            checked={Boolean(currentValue)}
            onCheckedChange={onChange}
            disabled={effectiveDisabled}
            aria-label={label}
          />
        </ParameterRow>
      )
    }

    // ============================================================================
    // TEXTAREA (Prompts)
    // ============================================================================
    case 'textarea': {
      return wrapWithLockIndicator(
        <div className="space-y-2">
          <ParameterLabel parameter={parameter} icon="message-square" />
          <Textarea
            value={String(currentValue || '')}
            onChange={(e) => onChange(e.target.value)}
            aria-label={label}
            placeholder={`Enter ${label.toLowerCase()}...`}
            disabled={effectiveDisabled}
            rows={3}
            className="resize-none"
          />
        </div>
      )
    }

    // ============================================================================
    // SEED INPUT (Revised - below, no dice)
    // ============================================================================
    case 'seed': {
      const min = parameter.minimum ?? 0
      const max = parameter.maximum ?? 2147483647
      const safeValue = (currentValue ?? min) as number

      return wrapWithLockIndicator(
        <div className="space-y-2">
          <ParameterLabel parameter={parameter} icon="hash" />
          <Input
            type="number"
            value={safeValue}
            onChange={(e) => onChange(Number(e.target.value))}
            aria-label={label}
            min={min}
            max={max}
            step={1}
            disabled={effectiveDisabled}
            className="w-full"
          />
        </div>
      )
    }

    // ============================================================================
    // MULTI-SELECT (Checkboxes for array parameters)
    // ============================================================================
    case 'multi-select': {
      const options = parameter.enum || []
      const selectedValues = Array.isArray(currentValue) ? (currentValue as unknown[]) : []

      const handleToggle = (option: unknown, checked: boolean) => {
        if (checked) {
          onChange([...selectedValues, option])
        } else {
          onChange(selectedValues.filter((v) => v !== option))
        }
      }

      return wrapWithLockIndicator(
        <div className="space-y-2">
          <ParameterLabel parameter={parameter} icon="list-checks" />
          {options.length > 0 ? (
            <div className="space-y-1.5 rounded-md border border-input p-2 max-h-40 overflow-y-auto">
              {options.map((option) => {
                const optionStr = String(option)
                const isChecked = selectedValues.some((v) => String(v) === optionStr)
                return (
                  <div key={optionStr} className="flex min-w-0 items-center gap-2">
                    <Checkbox
                      id={`${parameter.name}-${optionStr}`}
                      checked={isChecked}
                      onCheckedChange={(checked) => handleToggle(option, Boolean(checked))}
                      disabled={effectiveDisabled}
                      size="sm"
                    />
                    <Label
                      htmlFor={`${parameter.name}-${optionStr}`}
                      size="sm"
                      className="cursor-pointer font-normal text-muted-foreground"
                    >
                      {optionStr}
                    </Label>
                  </div>
                )
              })}
            </div>
          ) : (
            <Input
              value={Array.isArray(currentValue) ? (currentValue as string[]).join(', ') : String(currentValue || '')}
              onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              aria-label={label}
              placeholder="Enter values separated by commas..."
              disabled={effectiveDisabled}
              className="w-full"
            />
          )}
          {selectedValues.length > 0 && (
            <div className="text-xs text-muted-foreground">
              {selectedValues.length} selected
            </div>
          )}
        </div>
      )
    }

    // ============================================================================
    // SELECT-OR-INPUT (Dropdown with custom input option)
    // ============================================================================
    case 'select-or-input': {
      const options = parameter.enum || []
      const currentStr = currentValue !== undefined && currentValue !== null ? String(currentValue) : ''
      const isCustom = currentStr !== '' && !options.some((opt) => String(opt) === currentStr)

      return wrapWithLockIndicator(
        <SelectOrInputControl
          parameter={parameter}
          options={options}
          currentValue={currentStr}
          isInitiallyCustom={isCustom}
          onChange={onChange}
          disabled={effectiveDisabled}
        />
      )
    }

    // ============================================================================
    // ASPECT RATIO (Visual ratio picker with preset buttons)
    // ============================================================================
    case 'aspect-ratio': {
      const options = parameter.enum || ['1:1', '16:9', '9:16', '4:3', '3:4', '21:9']
      const currentStr = String(currentValue || parameter.default || '1:1')

      return wrapWithLockIndicator(
        <div className="space-y-2">
          <ParameterLabel parameter={parameter} icon="maximize" />
          <div className="grid grid-cols-3 gap-1.5">
            {(options as string[]).map((ratio) => {
              const isSelected = currentStr === ratio
              return (
                <button
                  key={ratio}
                  onClick={() => onChange(ratio)}
                  disabled={effectiveDisabled}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-md border px-2 py-1.5 text-xs transition-colors",
                    isSelected
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  )}
                >
                  <AspectRatioPreview ratio={ratio} />
                  <span>{ratio}</span>
                </button>
              )
            })}
          </div>
        </div>
      )
    }

    // ============================================================================
    // IMAGE SIZE (Select with visual size previews)
    // ============================================================================
    case 'image-size': {
      const options = parameter.enum || ['square', 'square_hd', 'landscape_4_3', 'portrait_4_3', 'landscape_16_9', 'portrait_16_9']
      const currentStr = String(currentValue || parameter.default || 'square_hd')

      // Map size presets to aspect ratios for visual preview
      const sizeToRatio: Record<string, string> = {
        'square': '1:1',
        'square_hd': '1:1',
        'landscape_4_3': '4:3',
        'portrait_4_3': '3:4',
        'landscape_16_9': '16:9',
        'portrait_16_9': '9:16',
      }

      // Format label: "landscape_4_3" → "Landscape 4:3"
      const formatLabel = (size: string) =>
        size.replace(/_/g, ' ').replace(/(\d+) (\d+)/g, '$1:$2')
          .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

      return wrapWithLockIndicator(
        <div className="space-y-2">
          <ParameterLabel parameter={parameter} icon="maximize" />
          <div className="grid grid-cols-3 gap-1.5">
            {(options as string[]).map((size) => {
              const isSelected = currentStr === size
              const ratio = sizeToRatio[size] || '1:1'
              return (
                <button
                  key={size}
                  onClick={() => onChange(size)}
                  disabled={effectiveDisabled}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-md border px-2 py-1.5 text-xs transition-colors",
                    isSelected
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  )}
                >
                  <AspectRatioPreview ratio={ratio} />
                  <span className="truncate w-full text-center">{formatLabel(String(size))}</span>
                </button>
              )
            })}
          </div>
        </div>
      )
    }

    // ============================================================================
    // INPUT (Text, fallback)
    // ============================================================================
    case 'input':
    default: {
      return wrapWithLockIndicator(
        <div className="space-y-2">
          <ParameterLabel parameter={parameter} icon="text" />
          <Input
            value={String(currentValue || '')}
            onChange={(e) => onChange(e.target.value)}
            placeholder={parameter.description || ''}
            disabled={effectiveDisabled}
            className="w-full"
          />
        </div>
      )
    }
  }
})
